import { FaSearch, FaCaretDown, FaWallet } from "react-icons/fa";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import defaultUserImg from "../assets/images/user.png";
import { API_BASE, BACKEND_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

export default function Topbar() {
  const location = useLocation();
  const hideSearch = ["/faqs", "/conditions", "/privacy", "/2257", "/dmca", "/content-contributor-agreement", "/terms"].includes(location.pathname);
  const { profile, setProfile, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!profile?.accessToken) {
        setLoading(false);
        return;
      }

      const activeToken = profile.accessToken;

      try {
        const res = await fetch(`${API_BASE}accounts/profile/me/`, {
          headers: { Authorization: `Bearer ${activeToken}` },
        });

        if (!res.ok) throw new Error("Failed to fetch profile");
        const data = await res.json();

        const safe = (url) => url && String(url) !== "null" && !String(url).includes("/null")
          ? (String(url).startsWith("http") ? url : `${BACKEND_BASE}${url}`)
          : null;
        setProfile((prev) => {
          if (!prev?.accessToken || prev.accessToken !== activeToken) {
            return prev;
          }

          const walletBalance = data.wallet_balance != null ? Number(data.wallet_balance) : (prev?.walletBalance ?? prev?.wallet_balance ?? 0);
          return {
            ...prev,
            screenName: data.screen_name || prev?.screenName || "User",
            email: data.email || prev?.email,
            role: data.role || prev?.role || "user",
            profilePicture: safe(data.profile_picture) ?? prev?.profilePicture,
            idDocument: safe(data.id_document) ?? prev?.idDocument,
            walletBalance,
            wallet_balance: walletBalance,
          };
        });
      } catch (err) {
        console.error("Topbar profile fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profile?.accessToken, setProfile]);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login"; // redirect
  };

  // Debounced search for contributors for dropdown
  const fetchSearchResults = (term) => {
    const q = (term || "").trim();
    if (!q) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    if (!profile?.accessToken) return;

    setSearchLoading(true);
    fetch(`${API_BASE}accounts/contributors/?search=${encodeURIComponent(q)}`, {
      headers: {
        Authorization: `Bearer ${profile.accessToken}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed search");
        const data = await res.json();
        const contributorsArray = data.contributors || data.results || (Array.isArray(data) ? data : []);
        const transformed = (contributorsArray || []).map((c) => ({
          id: c.id,
          name: c.screen_name || c.name,
          img: c.avatar || c.profile_picture || c.profilePicture,
          age: c.age,
        }));
        setSearchResults(transformed);
        setSearchOpen(true);
      })
      .catch((err) => {
        console.error("Topbar search error:", err);
        setSearchResults([]);
        setSearchOpen(false);
      })
      .finally(() => setSearchLoading(false));
  };

  const handleSelectContributor = (contributor) => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    // Navigate to Explore Content with initial search and pass contributor for immediate modal
    navigate(`/explore-content?search=${encodeURIComponent(contributor.name || "")}`, {
      state: { selectedContributor: contributor },
    });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <>
      <div className="topbar-wrapper">
      <style>{`
        .search-input {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: none;
          box-shadow: none;
        }
        .search-input::-webkit-search-cancel-button,
        .search-input::-webkit-search-decoration,
        .search-input::-webkit-search-results-button,
        .search-input::-webkit-search-results-decoration {
          display: none;
        }
        .search-input::-webkit-autofill,
        .search-input::-webkit-autofill:hover,
        .search-input::-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 30px #1C1C1E inset !important;
          -webkit-text-fill-color: grey !important;
        }
        .search-input:invalid {
          box-shadow: none;
        }
      `}</style>
      <div className="flex flex-col gap-4 mb-6 md:mb-12 pt-4 md:pt-0 w-full px-2 relative">
      <div className="flex items-center justify-between w-full">
        {/* Left Section: Empty space for hamburger menu */}
        <div className="w-12"></div>
        
        {/* Right Section: Wallet + User */}
        <div className="flex items-center gap-4 mt-[-16px]">
          {profile?.role === "user" && (
            <div className="flex items-center bg-[#2A2A2D] border border-[#CEB46A] text-[#CEB46A] px-3 py-1 rounded-full text-sm font-semibold shadow-sm">
              <FaWallet className="mr-1" />
              ${typeof (profile?.walletBalance ?? profile?.wallet_balance) === "number"
                ? (profile.walletBalance ?? profile.wallet_balance).toFixed(2)
                : "0.00"}
            </div>
          )}

          {/* User Dropdown */}
          <Popover className="relative">
            <PopoverButton as="div" className="flex items-center gap-2 cursor-pointer">
              <img
                src={(() => {
                  const u = profile?.profilePicture || profile?.profile_picture || profile?.idDocument;
                  return (u && String(u) !== "null" && !String(u).includes("/null")) ? u : defaultUserImg;
                })()}
                alt="User"
                className="w-8 h-8 rounded-full object-cover"
                referrerPolicy="no-referrer"
                onError={(e) => { e.target.src = defaultUserImg; }}
              />
              <div className="flex flex-col justify-center leading-tight">
                <p className="text-sm font-medium text-[#CEB46A] leading-none">
                  {loading ? "Loading..." : (profile?.screenName || profile?.screen_name || "User")}
                </p>
                <p className="text-xs text-gray-500 leading-none">
                  {loading ? "Loading..." : profile?.role || "Unknown"}
                </p>
              </div>
              <FaCaretDown className="text-[#CEB46A] mt-1" />
            </PopoverButton>
            <PopoverPanel className="absolute z-10 mt-2 right-0 bg-[#1C1C1E] rounded-xl w-36 p-2 shadow-md border border-gray-700/40">
              <button
                onClick={() => navigate("/my-info")}
                className="block w-full text-left px-4 py-2 rounded-md hover:bg-gray-800/60 text-sm text-white"
              >
                Settings
              </button>

              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 rounded-md hover:bg-gray-800/60 text-sm text-white"
              >
                Logout
              </button>
            </PopoverPanel>
          </Popover>
        </div>
      </div>

      {/* Search Bar with dropdown (hidden on FAQs and Terms & Conditions) */}
      {!hideSearch && (
      <div className="w-full flex justify-center" ref={searchContainerRef}>
        <div className="relative w-full max-w-md">
          <FaSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-[#CEB46A] z-10 pointer-events-none" />
          <input
            type="text"
            placeholder="Search contributors by screen name or name..."
            autoComplete="off"
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
              searchTimeoutRef.current = setTimeout(() => fetchSearchResults(value), 300);
            }}
            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
            className="search-input bg-[#1C1C1E] w-full text-[grey] pl-10 pr-4 py-2 border border-[#1C1C1E] rounded-md shadow-sm focus:outline-none focus:border-[#CEB46A] transition-colors"
          />
          {searchOpen && (searchQuery || "").trim() && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-[#1C1C1E] border border-gray-700 rounded-md shadow-xl max-h-64 overflow-y-auto z-50">
              {searchLoading ? (
                <div className="px-4 py-3 text-gray-400 text-sm">Searching...</div>
              ) : searchResults.length === 0 ? (
                <div className="px-4 py-3 text-gray-400 text-sm">No contributors found.</div>
              ) : (
                <ul className="py-1">
                  {searchResults.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectContributor(c)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-white hover:bg-[#2A2A2D] focus:bg-[#2A2A2D] focus:outline-none"
                      >
                        {c.img ? (
                          <img
                            src={c.img}
                            alt=""
                            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center text-[#CEB46A] font-semibold text-sm flex-shrink-0">
                            {(c.name || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate">
                            {c.name || "Unknown"}
                          </span>
                          {c.age != null && (
                            <span className="text-xs text-gray-400">Age {c.age}</span>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
      </div>
    </>
  );
}
